import store from "@/store";
import { err, ok, Result } from "neverthrow";
import { isEmpty } from "@/framework/utilities/isEmpty";
import { UserProfile } from "@/modules/auth/objects/userProfile";
import { AuthService } from "@/framework/api/AuthService";
import { MediaService } from "@/framework/media/MediaService";
import { ErrorService } from "@/framework/errors/ErrorService";
import { AuthenticationRepository, LoginAccept, LoginError } from "@/framework/repositories/authenticationRepository";
import { CommonVuexStateRepository } from "@/framework/repositories/CommonVuexStateRepository";
import { SettingsService } from "@/framework/api/Staff/Settings/SettingsService";
import { TenantName } from "@/framework/types/StaffLocal";
import { StaffService } from "@/framework/api/Staff/StaffService";
import { KtznEventSubscriptionAction } from "@/framework/store/ktznEventSubscription/types";
import { DEFAULT_LANG_CD } from "@/framework/constants/trans";
import { Trans } from "@/framework/plugins/translation";
import { i18n } from "@/framework/plugins/i18n";
import { datadogRum } from "@datadog/browser-rum";
import CustomError from "@/framework/errors/CustomError";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { StaffStore } from "@/modules/staff/objects/Staff";

export const ACTIVATION_BOT = process.env.VUE_APP_ACTIVATION_BOT;

type SignInResponse = {
  response: LoginAccept;
  userProfile: UserProfile;
};

export class UserService {
  private authenticationRepository: AuthenticationRepository;
  private vuexStateRepository: CommonVuexStateRepository;
  private errorService: ErrorService;

  constructor(
    authenticationRepository: AuthenticationRepository = new AuthenticationRepository(),
    vuexStateRepository: CommonVuexStateRepository = new CommonVuexStateRepository(),
    errorService: ErrorService = new ErrorService()
  ) {
    this.authenticationRepository = authenticationRepository;
    this.vuexStateRepository = vuexStateRepository;
    this.errorService = errorService;
  }

  /**
   * Accept sign in for normal and sandbox users
   * @param token - transaction code or one time token
   * @param isSandbox - is sandbox user
   */
  acceptSignInOnetime = async (token: string, isSandbox: boolean): Promise<Result<SignInResponse, LoginError>> => {
    const res = await this.authenticationRepository.getLoginAcceptResOnetime(token, isSandbox);
    return await this.doAcceptSignIn(res, true);
  };

  /**
   * Get qc_user information from local storage first (If user already signed in)
   * Otherwise, fetch qc_user information from API
   */
  getCachedQcUser = async (): Promise<Result<UserProfile, Error>> => {
    try {
      let stores = store.getters;
      if (!isEmpty(stores) && !isEmpty(stores.user) && Object.keys(stores.user).length > 0) {
        return ok(stores.user);
      } else {
        const user = await this.getQcUser();
        if (!isEmpty(user) && user != null) {
          return ok(user);
        } else {
          await this.authenticationRepository.signOutBeforeLogin();
          return err(new Error("User information cannot be fetched from stores or api"));
        }
      }
    } catch (error) {
      console.error("Error getCachedUser", error);
      await this.authenticationRepository.signOutUser(false);
      return err(new Error("Exception while fetching user information from stores or api"));
    }
  };

  /**
   * Get qc_user information from API
   * @param noCache - do not use cache
   */
  getQcUser = async (noCache: boolean = false): Promise<UserProfile | null> => {
    const res = await new AuthService().fetchQcUser(noCache);
    if (res.isOk()) {
      const qcUser = res.value;
      if (!qcUser) {
        return null;
      }

      let icon = new MediaService().resizeCloudinaryImage(qcUser.icon || "", 250) || qcUser.icon || "";
      const user: UserProfile = {
        id: qcUser.access_id,
        id_account: qcUser.id_account,
        username: qcUser.name,
        email: qcUser.email || "",
        userType: qcUser.user_type,
        langCd: qcUser.lang_cd,
        icon: icon,
        qr: qcUser.qr || "",
        emailNotification: qcUser.notification_email,
        pushNotification: qcUser.notification_push,
        isOfficial: qcUser.is_official || false,
        snsNotification: false,
        snsAccessId: "",
        snsType: "",
        activationType: qcUser.activation_type || "",
        signUpAt: qcUser.sign_up_at || "",
        sandboxType: qcUser.sandbox_type,
      };

      // after confirming user info prior, set user id for datadog RUM logs alongside store update
      datadogRum.setUser({
        id: qcUser && qcUser.access_id ? qcUser.access_id.toString() : "user missing",
      });

      this.vuexStateRepository.setQcUser(user);
      // await this.startWebsocketAndSubscribe(user.id);

      if (user.userType === "biz_staff") {
        this.vuexStateRepository.setAppMode("customer_service");
      } else {
        this.vuexStateRepository.setAppMode("group");
      }

      return user;
    } else {
      console.error("could not get getQcUser response", res.error);
      if (!store.getters.isUserLoggedOut) {
        await this.errorService.handleError(res.error);
      }
      return null;
    }
  };

  /**
   * Change language
   * @param langCd - language code
   * @param needUpdateBackend - update backend language
   */
  changeLanguage = async (langCd: string, needUpdateBackend: boolean = true) => {
    const updateFrontLanguage = async (lang_cd: string) => {
      await Trans.changeLanguage(lang_cd);
      Trans.currentLanguage = lang_cd;
      i18n.locale = lang_cd;
    };

    if (needUpdateBackend) {
      const settingService = new SettingsService();
      let params = {
        langCd: langCd,
      };
      const updateBackendLanguageRes = await settingService.updateUserSetting(params);
      if (updateBackendLanguageRes.isErr()) {
        let errorMessage =
          `*** update backend language error ***\n` +
          `*** so change language to "${DEFAULT_LANG_CD}"(Default language) ***`;
        console.error(errorMessage);
        params.langCd = DEFAULT_LANG_CD;
        await settingService.updateUserSetting(params);
        return this.errorService.handleError(updateBackendLanguageRes.error);
      }
    }

    try {
      await updateFrontLanguage(langCd);
    } catch (e) {
      let errorMessage =
        `*** update frontend language error ***\n` +
        `*** so change language to "${DEFAULT_LANG_CD}"(Default language) ***`;
      console.error(errorMessage);
      console.error(e);

      await updateFrontLanguage(DEFAULT_LANG_CD);
      throw err(new CustomError(CustomError.Type.general, errorMessage, "updateFrontLanguage"));
    }
  };

  /**
   * Accept sign in
   * @param transactionCode - transaction code
   * @param confirmationCode - confirmation code
   */
  acceptSignIn = async (
    transactionCode: string,
    confirmationCode: string
  ): Promise<Result<SignInResponse, LoginError>> => {
    const res = await this.authenticationRepository.getLoginAcceptRes(transactionCode, confirmationCode);
    return await this.doAcceptSignIn(res);
  };

  /**
   * Get staff information from cache or API
   * @param langCd - language code
   * @param forceUpdate - force update
   * @param fullResult - full result
   */
  getCachedStaff = async (
    langCd = "",
    forceUpdate = false,
    fullResult = false
  ): Promise<Result<StaffStore, GraphqlApiError>> => {
    if (!forceUpdate && store.getters.staff && Object.keys(store.getters.staff).length > 0) {
      return ok(store.getters.staff);
    }
    const _langCd = langCd == "" ? i18n.locale : langCd;
    const vars = { langCd: _langCd };

    // using the "fullResult" attr, allow user to grab complete "myself" object in the (rare?) event it's needed
    let res;
    const staffService = new StaffService();
    if (!fullResult) {
      res = await staffService.getBasicStaffDetails(vars);
    } else {
      res = await staffService.getStaffDetailsWithLocationAndMenus(vars);
    }

    if (res.isErr()) {
      return err(res.error);
    }

    this.vuexStateRepository.setStaffMyself({ myself: res.value });
    return ok({
      myself: res.value,
    });
  };

  /**
   * Get user access id
   */
  getUserAccessId = async (): Promise<Result<string, Error>> => {
    let stores = store.getters;
    let accessId;
    if (!isEmpty(stores) && !isEmpty(stores.user) && Object.keys(stores.user).length > 0) {
      accessId = stores.user.id;
      return ok(accessId);
    } else if (
      !isEmpty(stores) &&
      !isEmpty(stores.staff) &&
      !isEmpty(stores.staff.myself) &&
      Object.keys(stores.staff).length > 0
    ) {
      accessId = stores.staff.myself.user.id;
      return ok(accessId);
    } else {
      return err(new Error("user not found"));
    }
  };

  /**
   * Get tenant name
   * @param langCd - language code
   * @param tenantNames - tenant names
   */
  getTenantName = (langCd: string, tenantNames: TenantName[]): string => {
    if (!tenantNames || tenantNames.length < 1) {
      // TODO error handling
      console.log(`##### No tenant names`);
      return "";
    }
    const selectedTenantName: TenantName = tenantNames.filter((e) => e.lang_cd === langCd)[0];

    const result = selectedTenantName ? selectedTenantName.message : "";
    if (result) {
      return result;
    }

    const tenantNameEnglish = tenantNames.filter((e) => e.lang_cd === "en")[0];
    return tenantNameEnglish ? tenantNameEnglish.message : "";
  };

  async startWebsocketAndSubscribe(userId: string) {
    await this.vuexStateRepository.startWebSocket();
    await this.vuexStateRepository.subscribeToUser(userId);

    if (store && store.getters && store.getters.tenantId) {
      await store.dispatch(KtznEventSubscriptionAction.subscribeToTenant, store.getters.tenantId);
    }
  }

  private doAcceptSignIn = async (
    res: Result<LoginAccept, LoginError>,
    acceptOnetime: boolean = false
  ): Promise<Result<SignInResponse, LoginError>> => {
    if (res.isOk()) {
      // Stop any existing websocket connection
      await this.vuexStateRepository.stopWebSocket();
      // Clear auth store
      this.vuexStateRepository.clearAuthStore();

      const response = res.value;
      this.vuexStateRepository.setTokens(res.value);
      this.vuexStateRepository.setAuthStore();

      // Get qc_user information
      const userProfile = await this.getQcUser();
      if (userProfile == null) {
        return err({
          isError: true,
          errorMessage: "User information cannot be fetched from stores or api",
        });
      }

      if (!acceptOnetime) {
        // Get staff myself information
        const staff = await this.getCachedStaff(userProfile.langCd);
        if (staff.isErr()) {
          return err({
            isError: true,
            errorMessage: "Staff information cannot be fetched from stores or api",
          });
        }
      }

      await Trans.changeLanguage(userProfile.langCd);

      return ok({ response, userProfile });
    } else return err(res.error);
  };
}
